<script setup lang="ts">
import { type Props } from './types';
import Cross from '@/shared/ui/Icons/Cross.vue';

withDefaults(defineProps<Props>(), {
  placeholder: '',
  type: '',
  modelValue: '',
  disabled: false,
  error: '',
  attr: () => {},
  labelInsideText: '',
  clearable: false,
});

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'focus', event: FocusEvent): void;
  (e: 'blur', event: FocusEvent): void;
}>();

const isActive: Ref<boolean> = ref(false);

const handleInput = (e: Event): void => {
  const target = e.target as HTMLInputElement;
  emit('update:modelValue', target.value);
};

const focusHandler = (e: FocusEvent): void => {
  isActive.value = true;
  emit('focus', e);
};

const blurHandler = (e: FocusEvent): void => {
  isActive.value = false;
  emit('blur', e);
};

const clearInput = (): void => {
  emit('update:modelValue', '');
};
</script>

<template>
  <div class="app-input">
    <label v-if="labelInsideText" class="app-input__label">{{ labelInsideText }}</label>
    <input
      :value="modelValue"
      :class="[
        'app-input__field',
        { _error: error },
        { _disabled: disabled },
        { _active: isActive },
        { '_has-label': labelInsideText },
        { _clearable: clearable && modelValue },
      ]"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      v-bind="attr"
      @input="handleInput"
      @focus="focusHandler"
      @blur="blurHandler"
    />
    <span v-if="error" class="app-input__error">{{ error }}</span>
    <button
      v-if="clearable && modelValue"
      class="app-input__clear-button"
      :aria-label="t('clearInput')"
      @click="clearInput"
    >
      <Cross class="app-input__clear-button-icon" />
    </button>
  </div>
</template>

<style lang="less">
.app-input {
  position: relative;
  border-radius: @main-size;
  width: 100%;

  &__label {
    position: absolute;
    top: @size-x2;
    left: @size-x4;
    font-size: 10px;
    line-height: 100%;
    font-weight: 500;
    color: @color-dove-gray-secondary;
    z-index: 1;
    pointer-events: none;
  }

  &__field {
    .input-reset();

    font-size: @size-x4;
    font-weight: 400;
    line-height: 21px;
    color: @color-cod-gray;
    background-color: @color-athens-gray;
    border: 1px solid transparent;
    width: 100%;
    height: 50px;
    border-radius: @size-x2;
    padding: @size-x4;
    transition: border-color 0.3s ease;

    &._has-label {
      padding: @size-x5 @size-x4 @size-x2;
    }

    &::placeholder {
      color: @color-silver-chalice;
    }

    &._clearable {
      padding: @size-x3 30px @size-x3 @size-x4;
    }

    &._error {
      border: 1px solid @color-carnation;
    }

    &._disabled {
      pointer-events: none;
    }

    &._clearable {
      padding-right: @size-x10 !important;
    }

    &._active:not(._error) {
      border: 1px solid @color-tango !important;
    }

    &:not(._error)::active {
      border: 1px solid @color-tango;
    }
  }

  &__clear-button {
    .button-reset();

    display: inline-flex;
    position: absolute;
    top: 50%;
    right: @size-x4;
    transform: translateY(-50%);
  }

  &__clear-button-icon {
    width: 16px;
    height: 16px;
  }

  &__error {
    display: block;
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-carnation;
    margin-top: @size-x3;
  }
}
</style>
